import { ILogicRule, ILogicRuleAction, ILogicRuleCondition } from './@types/DexeroFD';

export interface IDexeroFormLogicRules {
  logicRulesHolder: { [key: string]: ILogicRule[] };
  applyLogicOnJsonTableFields(suffix?: string): void;
  applyLogicRule(fieldSuffix?: string): void;
  bindActionFields(): any;
  bindOnFormVariableCalculate(logicRulesArray: ILogicRule[], fieldSuffix?: string): void;
  checkCondition(
    logicRuleCondition: ILogicRuleCondition,
    fieldSuffix: string,
    theField: JQuery<HTMLInputElement>
  ): boolean;
  checkMatch(logicRule: ILogicRule, fieldSuffix?: string): void;
  doLogicRuleActions(logicRuleActions: ILogicRuleAction[], fieldSuffix?: string): void;
  evaluateDateExpression(expression: string): any;
  hasParentValueFor(fieldId: number | string, fieldSuffix?: string): boolean;
  hasPreviousValueFor(fieldId: number | string, fieldSuffix?: string): boolean;
  initializeLogicRule(logicRulesArray: ILogicRule[], fieldSuffix?: string): void;
  loadLogicRules(suffix?: string): void;
  mergeFieldValues(fieldValue: any): any;
  resolveFieldAction($field: IjQueryElm): Promise<void>;
  restoreDefaultFieldStates(logicRule: ILogicRule, fieldSuffix?: string): void;
  setFieldRequired(fieldId: string | number, isRequired: boolean, fieldSuffix?: string): void;
  setFieldVisible(fieldId: number | string, isVisible: boolean, fieldSuffix?: string): void;
  setFilterFieldValues(
    fieldId: string | number,
    filteredValueIds: string[],
    fieldSuffix?: string
  ): void;
  toggleTableRowColumn(formGroup: IjQueryElmOrHtmlElm): void;
}

const DexeroFormLogicRules = {
  logicRulesHolder: {} as { [key: string]: ILogicRule[] },

  // Apply logic rules on elements
  applyLogicOnJsonTableFields(fieldSuffix = ''): void {
    const $form = $(`form[data-suffix="${fieldSuffix}"]`);
    const $jsonTableField = $form.find('.field-table-group-container');
    const $hiddenFormGroups = $jsonTableField.find('.form-group.hide');
    $hiddenFormGroups.each((_, $formGroup) => {
      window.DexeroCoreApp.toggleTableRowColumn($formGroup);
    });
  },

  applyLogicRule(fieldSuffix = ''): void {
    const logicRulesKey = `logicRules${fieldSuffix}`;
    const logicRulesArray = this.logicRulesHolder[logicRulesKey];

    $.each(logicRulesArray, (_, logicRule) => {
      window.DexeroCoreApp.restoreDefaultFieldStates(logicRule, fieldSuffix);
    });
    $.each(logicRulesArray, (_, logicRule) => {
      window.DexeroCoreApp.checkMatch(logicRule, fieldSuffix);
    });
  },

  bindActionFields() {
    const $fields = $('body').find('[data-fire-field-actions="true"]:not(.action-bound)');
    $fields.each((_, elm) => {
      const $elm = $(elm);
      $elm.on('blur', () => window.DexeroCoreApp.resolveFieldAction($elm));
      $elm.addClass('action-bound');
    });
  },

  // Form variable updates, don't really update any fields, so it doesn't seem like we need this
  bindOnFormVariableCalculate(logicRulesArray: ILogicRule[], fieldSuffix = ''): void {
    $(document).bind(`onFormVariableCalculate${fieldSuffix}`, (e, responseData) => {
      let needLogicRuleRefresh = false;

      $.each(responseData, (key: string, value) => {
        if (key.startsWith('field_')) {
          const fieldId = key.substring(key.indexOf('_') + 1);
          const prevValues = (window as any)[
            `previousValues${fieldSuffix}`
          ] as IDCA.IPreviousValues[];

          if (window.DexeroCoreApp.hasPreviousValueFor(fieldId, fieldSuffix)) {
            for (const previousValue of prevValues) {
              if (String(previousValue.fieldId) === String(fieldId)) {
                ($ as any).log(
                  `Update previous value for fieldId = ${fieldId},  ${value} = ${value}`
                );

                previousValue.values = value;
              }
            }
          } else {
            ($ as any).log(`Add to previous value for fieldId = ${fieldId}, value = ${value}`);

            prevValues[prevValues.length] = { fieldId: Number(fieldId), values: value };
          }
          needLogicRuleRefresh = true;
        }
      });

      if (needLogicRuleRefresh) {
        window.DexeroCoreApp.applyLogicRule(fieldSuffix);
      }
    });
  },

  checkCondition(
    logicRuleCondition: ILogicRuleCondition,
    fieldSuffix = '',
    theField: JQuery<HTMLInputElement>
  ): boolean {
    try {
      const isDateExpression =
        logicRuleCondition.operation === 'greaterExp' ||
        logicRuleCondition.operation === 'lowerExp';
      let fieldValue = '';
      if (logicRuleCondition.type === 0) {
        // Field
        fieldValue = window.DexeroCoreApp.getFieldRealValue(
          theField,
          String(logicRuleCondition.fieldId),
          fieldSuffix,
          isDateExpression
        );
      } else if (logicRuleCondition.type === 1) {
        // Role
        fieldValue = window.sessionUser.role;
      } else if (logicRuleCondition.type === 2) {
        // Group
        fieldValue = window.sessionUser.groups;
      }

      ($ as any).log(
        `logicRuleCondition.fieldId = ${logicRuleCondition.fieldId}, logicRuleCondition.type = ${logicRuleCondition.type}, fieldValue = ${fieldValue}`
      );

      if (logicRuleCondition.operation === 'equal') {
        if (Array.isArray(fieldValue)) {
          for (const fieldVal of fieldValue) {
            if (String(fieldVal) === String(logicRuleCondition.value)) {
              return true;
            }
          }
          return false;
        }
        const mergedValue = window.DexeroCoreApp.mergeFieldValues(fieldValue) || '';
        return mergedValue === String(logicRuleCondition.value);
      }

      if (logicRuleCondition.operation === 'notEqual') {
        if (Array.isArray(fieldValue)) {
          for (const fieldVal of fieldValue) {
            if (String(fieldVal) === String(logicRuleCondition.value)) {
              return false;
            }
          }
          return true;
        }
        const mergedValue = window.DexeroCoreApp.mergeFieldValues(fieldValue) || '';
        return mergedValue !== String(logicRuleCondition.value);
      }

      if (logicRuleCondition.operation === 'startsWith') {
        return fieldValue.startsWith(logicRuleCondition.value);
      }

      if (logicRuleCondition.operation === 'endsWith') {
        return fieldValue.endsWith(logicRuleCondition.value);
      }

      if (logicRuleCondition.operation === 'contains') {
        return fieldValue.indexOf(logicRuleCondition.value) >= 0;
      }

      if (logicRuleCondition.operation === 'empty') {
        return (
          fieldValue === null ||
          fieldValue === '' ||
          !fieldValue.length ||
          fieldValue === 'undefined'
        );
      }

      if (logicRuleCondition.operation === 'notEmpty') {
        return (
          fieldValue !== null &&
          fieldValue !== '' &&
          fieldValue.length !== 0 &&
          fieldValue !== 'undefined'
        );
      }

      if (logicRuleCondition.operation === 'selectedCount') {
        if (logicRuleCondition.value) {
          if (logicRuleCondition.value.substring(0, 1) === '>') {
            const intValue = parseInt(logicRuleCondition.value.substring(1), 10);
            if (intValue >= 0) {
              if (Array.isArray(fieldValue)) {
                return fieldValue.length > intValue;
              }
              if (fieldValue !== '') {
                return intValue < 1;
              }
            }
          } else if (logicRuleCondition.value.substring(0, 1) === '<') {
            const intValue = parseInt(logicRuleCondition.value.substring(1), 10);
            if (intValue >= 0) {
              if (Array.isArray(fieldValue)) {
                return fieldValue.length < intValue;
              }
              if (fieldValue !== '') {
                return intValue < 1;
              }
            }
          } else if (logicRuleCondition.value.indexOf(':') > 0) {
            const parts = logicRuleCondition.value.split(':');
            if (parts.length === 2) {
              const min = parseInt(parts[0], 10);
              const max = parseInt(parts[1], 10);
              let count = 0;
              if (Array.isArray(fieldValue)) {
                count = fieldValue.length;
              } else if (fieldValue !== '') {
                count = 1;
              }
              return count > min && count < max;
            }
          } else {
            const intValue = parseInt(logicRuleCondition.value);
            const fieldValueCount = Array.isArray(fieldValue) ? fieldValue.length : 0;
            return intValue === fieldValueCount;
          }
        }
      } else if (logicRuleCondition.operation === 'one') {
        const conditionValues = logicRuleCondition.value.split(',');
        if (Array.isArray(fieldValue)) {
          let result = false;
          $.each(fieldValue, (index, value) => {
            let find = false;
            for (const conditionValue of conditionValues) {
              if (String(value) === String(conditionValue)) {
                find = true;
                break;
              }
            }
            if (find) {
              result = true;
            }
          });
          return result;
        }
        for (const conditionValue of conditionValues) {
          if (String(fieldValue) === String(conditionValue)) {
            return true;
          }
        }
        return false;
      } else if (logicRuleCondition.operation === 'all') {
        const conditionValues = logicRuleCondition.value.split(',');
        if (Array.isArray(fieldValue)) {
          let result = true;
          for (const conditionValue of conditionValues) {
            let find = false;
            $.each(fieldValue, (index, value) => {
              if (String(value) === String(conditionValue)) {
                find = true;
              }
            });
            if (!find) {
              result = false;
              break;
            }
          }
          return result;
        }
        if (conditionValues.length > 1) {
          return false;
        }
        return String(fieldValue) === String(logicRuleCondition.value);
      } else if (logicRuleCondition.operation === 'none') {
        if (fieldValue === null || fieldValue === '') {
          return true;
        }
        const conditionValues = logicRuleCondition.value.split(',');
        if (Array.isArray(fieldValue)) {
          let result = true;
          $.each(fieldValue, (index, value) => {
            for (const conditionValue of conditionValues) {
              if (String(value) === String(conditionValue)) {
                result = false;
              }
            }
          });
          return result;
        }
        for (const conditionValue of conditionValues) {
          if (String(fieldValue) === String(conditionValue)) {
            return false;
          }
        }
        return true;
      } else if (logicRuleCondition.operation === 'greater') {
        return (
          window.DexeroCoreApp.convertStringToNumber(
            window.DexeroCoreApp.mergeFieldValues(fieldValue)
          ) > window.DexeroCoreApp.convertStringToNumber(logicRuleCondition.value)
        );
      } else if (logicRuleCondition.operation === 'lower') {
        return (
          window.DexeroCoreApp.convertStringToNumber(
            window.DexeroCoreApp.mergeFieldValues(fieldValue)
          ) < window.DexeroCoreApp.convertStringToNumber(logicRuleCondition.value)
        );
      } else if (logicRuleCondition.operation === 'greaterExp') {
        ($ as any).log(
          `${parseInt(fieldValue, 10)} VS ${window.DexeroCoreApp.evaluateDateExpression(
            logicRuleCondition.value
          )}`
        );

        return (
          parseInt(fieldValue, 10) >
          parseInt(window.DexeroCoreApp.evaluateDateExpression(logicRuleCondition.value), 10)
        );
      } else if (logicRuleCondition.operation === 'lowerExp') {
        ($ as any).log(
          `${parseInt(fieldValue, 10)} VS ${window.DexeroCoreApp.evaluateDateExpression(
            logicRuleCondition.value
          )}`
        );

        return (
          parseInt(fieldValue, 10) <
          parseInt(window.DexeroCoreApp.evaluateDateExpression(logicRuleCondition.value), 10)
        );
      }
    } catch (e) {
      ($ as any).log(e);
    }
    return false;
  },

  checkMatch(logicRule: ILogicRule, fieldSuffix = ''): void {
    if (logicRule && logicRule.conditions) {
      let allConditionMatch = true;
      let conditionMatch = false;
      let atLeastOneConditionMatch = false;
      for (const logicRuleCondition of logicRule.conditions) {
        const theField = $(
          `:input[name^="field_${logicRuleCondition.fieldId}${fieldSuffix}"]:not([name$="_old"]):not([name$="_reset"]):not([name$="_base64"])`
        ) as JQuery<HTMLInputElement>;
        if (
          theField ||
          window.DexeroCoreApp.hasPreviousValueFor(logicRuleCondition.fieldId, fieldSuffix) ||
          window.DexeroCoreApp.hasParentValueFor(logicRuleCondition.fieldId, fieldSuffix)
        ) {
          conditionMatch = window.DexeroCoreApp.checkCondition(
            logicRuleCondition,
            fieldSuffix,
            theField
          );
          if (conditionMatch) {
            ($ as any).log(
              `Condition match for field id = ${logicRuleCondition.fieldId} and isLinkByOr`
            );
            atLeastOneConditionMatch = true;
            if (logicRule.linkByOr) {
              break;
            }
            continue;
          } else {
            ($ as any).log(`Condition does not match for field id = ${logicRuleCondition.fieldId}`);
            allConditionMatch = false;
            if (!logicRule.linkByOr) {
              break;
            }
          }
        } else {
          ($ as any).log(`Cant find field field_${logicRuleCondition.fieldId}${fieldSuffix}`);
        }
      }
      if (
        (allConditionMatch && !logicRule.linkByOr) ||
        (atLeastOneConditionMatch && logicRule.linkByOr)
      ) {
        ($ as any).log(
          `allConditionMatch apply positive actions, logicRule.positiveActions.length = ${(logicRule.positiveActions as ILogicRuleAction[]).length
          }`
        );

        window.DexeroCoreApp.doLogicRuleActions(
          logicRule.positiveActions as ILogicRuleAction[],
          fieldSuffix
        );
      } else {
        ($ as any).log(
          `not allConditionMatch apply negative actions, logicRule.negativeActions.length = ${logicRule.negativeActions ? logicRule.negativeActions.length : ''
          }`
        );

        window.DexeroCoreApp.doLogicRuleActions(
          logicRule.negativeActions as ILogicRuleAction[],
          fieldSuffix
        );
      }
    }
  },

  doLogicRuleActions(logicRuleActions: ILogicRuleAction[], fieldSuffix = ''): void {
    if (!logicRuleActions) {
      return;
    }
    for (const logicRuleAction of logicRuleActions) {
      if (logicRuleAction.type === 1) {
        // Visible type

        ($ as any).log(
          `setFieldVisible(${logicRuleAction.fieldId}${fieldSuffix}, ${logicRuleAction.visible})`
        );

        window.DexeroCoreApp.setFieldVisible(
          logicRuleAction.fieldId as number,
          logicRuleAction.visible as boolean,
          fieldSuffix
        );
      } else if (logicRuleAction.type === 3) {
        // Filtered values type

        ($ as any).log(
          `FilterValues(${logicRuleAction.fieldId}${fieldSuffix}, ${logicRuleAction.filteredValueIds})`
        );

        window.DexeroCoreApp.setFilterFieldValues(
          logicRuleAction.fieldId as number,
          logicRuleAction.filteredValueIds as any as string[],
          fieldSuffix
        );
      } else if (logicRuleAction.type === 4) {
        // Required type

        ($ as any).log(
          `setFieldRequired(${logicRuleAction.fieldId}${fieldSuffix}, ${logicRuleAction.required})`
        );

        window.DexeroCoreApp.setFieldRequired(
          logicRuleAction.fieldId as number,
          logicRuleAction.required as boolean,
          fieldSuffix
        );
      } else if (logicRuleAction.type === 9) {
        // Empty warning formValidation
        const fieldValue = window.DexeroCoreApp.getFieldRealValue(
          false,
          `${logicRuleAction.fieldId}${fieldSuffix}`,
          fieldSuffix
        );

        ($ as any).log(`Empty warning formValidation fieldValue = ${fieldValue}`);

        if (logicRuleAction.emptyWarning && (fieldValue === null || fieldValue === '')) {
          window.confirm(logicRuleAction.message);
        }
      }
    }
  },

  evaluateDateExpression(expression: string): any {
    /* eslint-disable no-template-curly-in-string */
    const result = expression
      .replace('${now}', String(new Date().getTime()))
      .replace('${oneMinute}', String(60 * 1000))
      .replace('${oneHour}', String(60 * 60 * 1000))
      .replace('${oneDay}', String(24 * 60 * 60 * 1000));
    /* eslint-enable no-template-curly-in-string */

    ($ as any).log(`evaluateDateExpression result before = ${result}`);
    // Not sure why we have eval here
    // eslint-disable-next-line no-eval
    const evaluatedResult = eval(result);

    ($ as any).log(`evaluateDateExpression result after = ${evaluatedResult}`);

    return evaluatedResult;
  },

  hasParentValueFor(fieldId: number | string, fieldSuffix = ''): boolean {
    const parentValues = (window as any)[`parentValues${fieldSuffix}`] as IDCA.IPreviousValues[];
    for (const parentValue of parentValues) {
      if (String(parentValue.fieldId) === String(fieldId)) {
        return true;
      }
    }
    return false;
  },

  hasPreviousValueFor(fieldId: number | string, fieldSuffix = ''): boolean {
    const prevValues = (window as any)[`previousValues${fieldSuffix}`] as IDCA.IPreviousValues[];
    for (const previousValue of prevValues) {
      if (String(previousValue.fieldId) === String(fieldId)) {
        return true;
      }
    }
    return false;
  },

  // Bind elements to logic rules
  initializeLogicRule(logicRulesArray: ILogicRule[], fieldSuffix = ''): void {
    const logicRulesKey = `logicRules${fieldSuffix}`;
    this.logicRulesHolder[logicRulesKey] = logicRulesArray;

    $.each(this.logicRulesHolder[logicRulesKey], (index, logicRule) => {
      if (logicRule) {
        ($ as any).log(
          `logicRule.conditions.length = ${(logicRule.conditions as ILogicRuleCondition[]).length}`
        );

        for (const logicRuleCondition of logicRule.conditions as ILogicRuleCondition[]) {
          $(`:input[name^="field_${logicRuleCondition.fieldId}${fieldSuffix}"]`).each((_, elm) => {
            const $elm = $(elm);
            const isAlreadyBound = $elm.hasClass('logic-bound');
            const fireFieldActions = $elm.data('fire-field-actions');

            if (isAlreadyBound) return;
            $elm.addClass('logic-bound');

            let timeout: any;

            ($ as any).log(`Adding on change for field = ${$elm.attr('name')}`);

            $elm.on('change', () => {
              clearTimeout(timeout);
              timeout = fireFieldActions
                ? setTimeout(() => window.DexeroCoreApp.resolveFieldAction($elm), 500)
                : null;
              window.DexeroCoreApp.applyLogicRule(fieldSuffix);
            });
          });
        }
      }
      window.DexeroCoreApp.checkMatch(logicRule, fieldSuffix);
    });
  },

  loadLogicRules(suffix?: string): void {
    const _fieldSuffix = suffix || window.fieldSuffix;

    ($ as any).log('loadLogicRules fire');

    try {
      window.DexeroCoreApp.bindActionFields();
      const logicRulesArray = (window as any)[`logicRules${_fieldSuffix}`] as ILogicRule[];

      if (typeof logicRulesArray === 'object' && logicRulesArray.length) {
        window.DexeroCoreApp.initializeLogicRule(logicRulesArray, _fieldSuffix);
        // window.DexeroCoreApp.bindOnFormVariableCalculate(logicRulesArray, _fieldSuffix);
      }
    } catch (e) {
      console.error(e);
    }
  },

  mergeFieldValues(fieldValue: any): any {
    if (Array.isArray(fieldValue)) {
      let mergedFieldValue = '';
      $.each(fieldValue, (_, value) => {
        mergedFieldValue += value;
      });
      return mergedFieldValue;
    }
    return fieldValue;
  },

  resolveFieldAction($field: JQuery) {
    return new Promise<void>((resolve, reject) => {
      const $form = $field.closest('form') as JQuery<HTMLFormElement>;
      const suffix = $form.data('suffix') || '';
      const formIdentifier = $form.attr('id');
      const fieldIdentifier = $field.data('field-identifier');

      if (!formIdentifier || !fieldIdentifier) {
        ($ as any).log(
          `Unable to resolve field actions. FIELD: ${fieldIdentifier}. FORM: ${formIdentifier}`
        );
        resolve();
        return;
      }

      const { ownerIdentifier } = window.DexeroCoreApp.userOptions;
      const lang = window.language;

      (async () => {
        let formValues = $form.serialize();

        if (typeof window.DexeroCoreApp.onBeforeFieldActions === 'function') {
          formValues = await window.DexeroCoreApp.onBeforeFieldActions(formValues);
        }
        const body = window.DexeroCoreApp.getFormValuesObj(formValues, { dateForWebservice: true });

        const Swagger = await window.DexeroCoreApp.getSwaggerClient();

        const swaggerObj = {
          body,
          fieldIdentifier,
          formIdentifier,
          lang,
          ownerIdentifier,
        };

        const request = await Swagger.apis.forms.proceedOnFieldActions(swaggerObj);
        const response = request.obj;

        const { formVariableValues, logicRules, needFormVariableRefresh, needLogicRuleRefresh } =
          response;
        const logicRulesKey = `logicRules${suffix}`;

        if (needFormVariableRefresh) {
          $.each(formVariableValues, (key: string, value) => {
            const fieldId = window.DexeroCoreApp.getFieldIdForIdentifier(key, suffix);

            // Select variable paragraph
            const selector = `#field_${fieldId}${suffix}`;
            const $p = $(selector);

            $p.html(value);

            const prevValues = (window as any)[`previousValues${suffix}`] as IDCA.IPreviousValues[];

            if (window.DexeroCoreApp.hasPreviousValueFor(fieldId, suffix)) {
              for (const previousValue of prevValues) {
                if (String(previousValue.fieldId) === String(fieldId)) {
                  ($ as any).log(
                    `Update previous value for fieldId = ${fieldId},  ${value} = ${value}`
                  );

                  previousValue.values = value;
                }
              }
            } else {
              ($ as any).log(`Add to previous value for fieldId = ${fieldId}, value = ${value}`);

              prevValues[prevValues.length] = { fieldId: Number(fieldId), values: value };
            }
          });
          // Should no longer be needed
          // $(document).trigger(`onFormVariableCalculate${suffix}`, [data]);
          // $(document).trigger(`onFormVariableCalculate${suffix}`, formVariableValues);
        }

        // If we need to update the logicRules, we flush them and run them again
        if (needLogicRuleRefresh) {
          this.logicRulesHolder[logicRulesKey] = logicRules;
        }
        window.DexeroCoreApp.applyLogicRule(suffix);
      })()
        .then(resolve)
        .catch((e) => ($ as any).log(e));
    });
  },

  restoreDefaultFieldStates(logicRule: ILogicRule, fieldSuffix = ''): void {
    if (logicRule.positiveActions) {
      for (const logicRuleAction of logicRule.positiveActions) {
        const field = window.DexeroCoreApp.getFieldProperties(
          String(logicRuleAction.fieldId),
          fieldSuffix
        );
        if (field !== null) {
          if (logicRuleAction.type === 1) {
            // Visible type
            window.DexeroCoreApp.setFieldVisible(
              String(logicRuleAction.fieldId),
              field.visible,
              fieldSuffix
            );
          } else if (logicRuleAction.type === 3) {
            // Filtered values type
            if (field.displayType === 2) {
              // Select box
              ($ as any).log(
                `restoreDefaultFieldStates, :input[name^="field_${logicRuleAction.fieldId}${fieldSuffix}"] > option`
              );

              if (window.DexeroCoreApp.browser.msie) {
                (
                  $(`:input[name^="field_${logicRuleAction.fieldId}${fieldSuffix}"] span`) as any
                ).showOption();
              } else {
                $(
                  `:input[name^="field_${logicRuleAction.fieldId}${fieldSuffix}"] option`
                ).removeClass('hide');
              }
            } else if (field.displayType === 1 || field.displayType === 3) {
              // radio or checkbox
              ($ as any).log(
                `restoreDefaultFieldStates, *[id^="fieldItem_${logicRuleAction.fieldId}${fieldSuffix}"]`
              );

              $(`*[id^="fieldItem_${logicRuleAction.fieldId}${fieldSuffix}"]`).removeClass('hide');
            } else if (field.displayType === 4) {
              // tree

              ($ as any).log(
                `restoreDefaultFieldStates, #tree_${logicRuleAction.fieldId}${fieldSuffix}`
              );

              ($(`#tree_${logicRuleAction.fieldId}${fieldSuffix}`) as any)
                .fancytree('getTree')
                .clearFilter();
            }
          } else if (logicRuleAction.type === 4) {
            // Required type
            window.DexeroCoreApp.setFieldRequired(
              logicRuleAction.fieldId as number,
              field.required,
              fieldSuffix
            );
          }
        } else {
          ($ as any).log('field is null!');
        }
      }
    }
  },

  setFieldRequired(fieldId: string | number, isRequired: boolean, fieldSuffix = ''): void {
    const formGroup = $(`#row_field_${fieldId}${fieldSuffix}`);
    const requiredSpan = $(formGroup).find(`#req_field_${fieldId}${fieldSuffix}`);
    const requiredSpanContent = window.DexeroCoreApp.removeSpacesAndLineBreaks(requiredSpan.html());
    const input = $(formGroup).find(':input');
    // const wysiwyg textarea;
    const noteEditable = $(formGroup).find('.note-editable');

    // Clear any errors
    if (!isRequired) {
      window.DexeroCoreApp.clearFieldError(String(fieldId), fieldSuffix);
    }

    // If required span does not exist, return
    if (!requiredSpan.length) {
      return;
    }
    // Used for javascript validation
    if (Object.hasOwnProperty.call(window, 'overrideRequiredFields')) {
      let found = false;
      if (window.overrideRequiredFields) {
        for (const overrideRequiredField of window.overrideRequiredFields) {
          if (String(overrideRequiredField.fieldId) === `${fieldId}${fieldSuffix}`) {
            found = true;
            overrideRequiredField.required = isRequired;
          }
        }
        if (!found) {
          ($ as any).log('overrideRequiredFields not found');

          window.overrideRequiredFields[window.overrideRequiredFields.length] = {
            fieldId: `${fieldId}${fieldSuffix}`,
            required: isRequired,
          };
        }
      }
    }

    if (requiredSpanContent !== '' && !isRequired) {
      requiredSpan.html('');
      $(input).prop('required', false).attr('aria-required', 'false');
      $(noteEditable).removeClass('required');
      formGroup.removeClass('required');
    } else if ((requiredSpanContent === '' || requiredSpanContent === '&nbsp;') && isRequired) {
      requiredSpan.html('*');
      $(input).prop('required', true).attr('aria-required', 'true');
      $(noteEditable).addClass('required');
      formGroup.addClass('required');
    }
  },

  // Last param was: resetValue?: boolean */
  setFieldVisible(fieldId: number | string, isVisible: boolean, fieldSuffix = ''): void {
    const $ele = $(`#row_field_${fieldId}${fieldSuffix}`);
    const hasFieldTableGroupField = $ele.find('.field-table-group-field').length;
    // const field = window.DexeroCoreApp.getFieldProperties(String(fieldId), fieldSuffix);
    // const $field = $(`*[name="field_${fieldId}${fieldSuffix}"]`);
    // const isDisplayOnlyField = $field.hasClass('display-only-input-value');
    // const $displayOnlyText = $field.siblings('.display-only-display-value');
    if (!$ele.length) {
      return;
    }
    if (isVisible) {
      $ele.removeClass('hide').attr('aria-hidden', 'false').trigger('formGroupVisible');
    } else {
      $ele.addClass('hide').attr('aria-hidden', 'true').trigger('formGroupHidden');
      // Do not reset values on hide anymore, to allow saving of values even when hidden
      /*
      if (resetValue && 0 === 1) {
        try {
          ($ as any).log(field);

          if (field && !field.keepValueOnHide) {
            if (field && field.defaultValue && field.defaultValue !== '') {
              if (field.type === 0) {
                $field.resetValue();
              } else if (field.type !== 1 || $field.nodeName.toLowerCase() === 'select') {
                $field.val(field.defaultValue);
                if (isDisplayOnlyField) {
                  $displayOnlyText.text(field.defaultValue);
                }
              } else {
                $field.resetValue();
                $(`#valueList_${field.defaultValue}`).prop('checked', true);
              }
            } else {
              $field.resetValue();
              $(`*[name="auto_field_${fieldId}${fieldSuffix}"]`).resetValue();
              if (isDisplayOnlyField) {
                $displayOnlyText.empty();
              }
            }
          }
        } catch (e) {
          ($ as any).log(`line 359 : ${e}`);
        }
      }
      */
    }
    $('body').trigger('onFormLayoutChange');

    if (hasFieldTableGroupField) {
      window.DexeroCoreApp.toggleTableRowColumn($ele);
    }
  },

  setFilterFieldValues(
    fieldId: string | number,
    filteredValueIds: string[],
    fieldSuffix = ''
  ): void {
    const field = window.DexeroCoreApp.getFieldProperties(String(fieldId), fieldSuffix);
    const $field = $(`:input[name^="field_${fieldId}${fieldSuffix}"]`);
    if (field !== null) {
      if (field.displayType === 2) {
        // Select box

        ($ as any).log(
          `setFilterFieldValues select box, filteredValueIds.length = ${filteredValueIds.length}`
        );

        // $(":input[name^=field_" + fieldId + fieldSuffix + "]").val("");
        for (const filteredValueId of filteredValueIds) {
          if (window.DexeroCoreApp.browser.msie) {
            ($field.find(`> option[value="${filteredValueId}"]`) as any).hideOption();
          } else {
            $field.find(`> option[value="${filteredValueId}"]`).addClass('hide');
          }
        }
      } else if (field.displayType === 1 || field.displayType === 3) {
        // radio or checkbox

        ($ as any).log(
          `setFilterFieldValues radio or checkbox, filteredValueIds.length = ${filteredValueIds.length}`
        );

        for (const filteredValueId of filteredValueIds) {
          $(`#fieldItem_${fieldId}${fieldSuffix}_${filteredValueId}`).addClass('hide');
          $(`#valueList_${filteredValueId}${fieldSuffix}`).prop('checked', false);
        }
      } else if (field.displayType === 4) {
        // tree

        ($ as any).log(
          `setFilterFieldValues tree, filteredValueIds.length = ${filteredValueIds.length}`
        );

        try {
          ($(`#tree_${fieldId}${fieldSuffix}`) as any)
            .fancytree('getTree')
            .filterNodes((node: any) => {
              let filterMatch = false;
              // Filter matches need to be hidden
              for (const filteredValueId of filteredValueIds) {
                if (node.key === `valueList_${filteredValueId}${fieldSuffix}`) {
                  filterMatch = true;
                  break;
                }
              }
              // If keepValueOnHide is false, un-check node before hiding
              if (filterMatch && !field.keepValueOnHide) {
                node.setSelected(false);
              }
              // Fancytree wants truthy return to SHOW, so we have to return the inverse
              return !filterMatch;
            });
        } catch (e) {
          console.warn(e);
        }
      }
    }
  },

  toggleTableRowColumn(formGroup: IjQueryElmOrHtmlElm): void {
    const $formGroup = $(formGroup);
    const table = $formGroup.closest('table.field-table-group-table');
    const $parent = $formGroup.parent(); // TD or TH

    const columnNumber = $parent.index() + 1; // Index is 0 based
    const row = $parent.parent('tr');
    const rowTds = $(row).find(' > th, > td');
    const colTds = $(table).find(
      `> thead > tr > th:nth-child(${columnNumber}),
       > thead > tr > td:nth-child(${columnNumber}),
       > tbody > tr > td:nth-child(${columnNumber}),
       > tbody > tr > th:nth-child(${columnNumber}),
       > tfoot > tr > td:nth-child(${columnNumber}),
       > tfoot > tr > th:nth-child(${columnNumber})`
    );

    let hideRow = true;
    let hideColumn = true;

    $(rowTds).each((_, td) => {
      const $fmgGroup = $(td).find('.form-group');
      const isHidden = $fmgGroup.hasClass('hide');
      if ($fmgGroup.length && !isHidden) {
        hideRow = false;
        return false;
      }
      return;
    });

    $(colTds).each((_, td) => {
      const $fmgGroup = $(td).find('.form-group');
      const isHidden = $fmgGroup.hasClass('hide');
      if ($fmgGroup.length && !isHidden) {
        hideColumn = false;
        return false;
      }
      return;
    });

    if (hideRow) {
      $(rowTds).parent('tr').addClass('hide').attr('aria-hidden', 'true');
    } else {
      $(rowTds).parent('tr').removeClass('hide').attr('aria-hidden', 'false');
    }

    if (hideColumn) {
      $(colTds).addClass('hide').attr('aria-hidden', 'true');
    } else {
      $(colTds).removeClass('hide').attr('aria-hidden', 'false');
    }
  },
};

export default DexeroFormLogicRules;
